import PropertyPicture from 'components/domain/property/picture/PropertyPicture';
import {
  GetIntegrationAgencySettingsResponseTO,
  IntegrationEnum,
  MuSupportType,
} from 'models/InternalIntegrationSetting';
import { isBusinessTypeSupported } from 'models/InternalIntegrationSetting.utils';
import { PropertyForPropertyListItem } from 'pages/agencyProperties/Properties.types';
import PropertyCollapsibleArea from 'pages/agencyProperties/item/PropertyCollapsibleArea';
import { useChildProperties } from 'pages/agencyProperties/item/PropertyItem.utils';
import { Row } from 'react-bootstrap';
import Badge from 'components/badge/Badge';
import theme from 'styles/theme';
import { useTranslation } from 'react-i18next';
import PropertyInfo from '../PropertyInfo';
import IntegrationMetadata from './integrationMetadata/IntegrationMetadata';
import {
  ActionsColumnStyled,
  PropertyDetailsColumnStyled,
  PropertyListItemStyled,
} from './PropertyListItem.styles';
import PropertyToggle from './propertyToggle/PropertyToggle';
import LockAssignmentSelect from './lockAssignmentSelect/LockAssignmentSelect';

interface PropertyListItemProps {
  property: PropertyForPropertyListItem;
  isChildProperty?: boolean;
  integrationAgencySettings: GetIntegrationAgencySettingsResponseTO;
  pagePropertyUids: string[];
}

const PropertyListItem = ({
  property,
  isChildProperty,
  integrationAgencySettings,
  pagePropertyUids,
}: PropertyListItemProps) => {
  const { t } = useTranslation();
  const childProperties = useChildProperties(property);

  const isPropertyWithChildren = childProperties.length > 0;

  const isLockAssignmentIntegration =
    integrationAgencySettings.enumId === IntegrationEnum.POINTCENTRAL ||
    integrationAgencySettings.enumId === IntegrationEnum.REMOTELOCK;

  const isPropertySupported = isBusinessTypeSupported({
    muSupportType:
      integrationAgencySettings.integration?.muSupportType ??
      MuSupportType.NONE,
    businessType: property.businessType,
  });

  const getActions = () => {
    if (!isPropertySupported) {
      return null;
    }

    if (isLockAssignmentIntegration) {
      return (
        <LockAssignmentSelect
          integrationAgencySettings={integrationAgencySettings}
          propertyUid={property.uid}
          propertyName={property.name}
          pagePropertyUids={pagePropertyUids}
        />
      );
    }

    return (
      <PropertyToggle
        integrationAgencySettings={integrationAgencySettings}
        propertyUid={property.uid}
        pagePropertyUids={pagePropertyUids}
      />
    );
  };

  return (
    <PropertyListItemStyled data-property-uid={property.uid}>
      <Row>
        <PropertyDetailsColumnStyled $isChildProperty={isChildProperty}>
          <PropertyPicture
            property={property}
            target="_blank"
            rel="noreferrer"
          />
          <PropertyInfo property={property} />
        </PropertyDetailsColumnStyled>

        <IntegrationMetadata
          propertyUid={property.uid}
          integration={integrationAgencySettings.integration}
          isPropertySupported={isPropertySupported}
          pagePropertyUids={pagePropertyUids}
        />

        <ActionsColumnStyled>
          {getActions()}

          {!property.isActive && (
            <Badge
              backgroundColor={theme.colors.gray600}
              data-testid="property-inactive-badge"
            >
              {t('pageIntegrations.integrationDetails.propertyInactive')}
            </Badge>
          )}
        </ActionsColumnStyled>
      </Row>

      {isPropertyWithChildren && (
        <PropertyCollapsibleArea property={property}>
          {childProperties.map((childProperty) => (
            <PropertyListItem
              key={childProperty.uid}
              property={childProperty}
              isChildProperty
              integrationAgencySettings={integrationAgencySettings}
              pagePropertyUids={pagePropertyUids}
            />
          ))}
        </PropertyCollapsibleArea>
      )}
    </PropertyListItemStyled>
  );
};

PropertyListItem.defaultProps = {
  isChildProperty: false,
};

export default PropertyListItem;
